import { FC, useEffect, useState } from 'react';
import {
  Card,
  Form,
  Select,
  Button,
  Switch,
  Modal,
  Avatar,
  Typography,
  Space,
  Divider,
  Row,
  Col,
  Tag,
  notification,
} from 'antd';
import { UserOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import {
  StudentAdmin,
  StudentUpdatePayload,
  SubsidyProgramAdmin,
} from '../../../../../models/admin/student-admin.model';
import { Grade } from '../../../../../models/grade.model';
import { School } from '../../../../../models/school.model';
import { ShirtSize } from '../../../../../models/shirt-size.model';
import { useGet, usePost, usePut } from '../../../../../hooks/useApi';

const { Title, Text } = Typography;
const { Option } = Select;

interface BasicTabProps {
  student: StudentAdmin;
  onUpdate: () => void;
}

const BasicTab: FC<BasicTabProps> = ({ student, onUpdate }) => {
  const [form] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();
  const [isSiteDirector, setIsSiteDirector] = useState<boolean>(false);

  // Check if user has Site Director context (accessContextId = 1)
  useEffect(() => {
    const checkSiteDirectorContext = () => {
      const auth = localStorage.getItem('auth');
      if (auth) {
        try {
          const authObj = JSON.parse(auth);
          setIsSiteDirector(authObj.selectedAccessContextId === 1);
        } catch {
          setIsSiteDirector(false);
        }
      }
    };
    checkSiteDirectorContext();
  }, []);

  const grades = useGet<Grade[]>('/v1/auth/dashboard/grades');
  const schools = useGet<School[]>('/v1/auth/dashboard/schools');
  const shirtSizes = useGet<ShirtSize[]>('/v1/auth/dashboard/shirt-sizes');
  const subsidyPrograms = useGet<SubsidyProgramAdmin[]>(
    '/v1/auth/admin/subsidy-programs',
    { enabled: true }
  );

  const updateMutation = usePut<StudentAdmin, StudentUpdatePayload>(
    `/v1/auth/admin/students/${student.id}`,
    {
      onSuccess: () => {
        notification.success({
          message: 'Success',
          description: 'Student information updated successfully.',
        });
        onUpdate();
      },
      onError: () => {
        notification.error({
          message: 'Error',
          description: 'Failed to update student information.',
        });
      },
    }
  );

  const subsidyToggleMutation = usePost<any, { eligible: boolean }>(
    `/v1/auth/admin/students/${student.id}/subsidy/toggle`,
    {
      onSuccess: () => {
        notification.success({
          message: 'Success',
          description: 'Subsidy eligibility updated successfully.',
        });
        onUpdate();
      },
      onError: () => {
        notification.error({
          message: 'Error',
          description: 'Failed to update subsidy eligibility.',
        });
      },
    }
  );

  const subsidyProgramMutation = usePost<
    any,
    { subsidyProgramId: number | null }
  >(`/v1/auth/admin/students/${student.id}/subsidy/program`, {
    onSuccess: () => {
      notification.success({
        message: 'Success',
        description: 'Subsidy program updated successfully.',
      });
      onUpdate();
    },
    onError: () => {
      notification.error({
        message: 'Error',
        description: 'Failed to update subsidy program.',
      });
    },
  });

  const expelMutation = usePost<any, { expelled: boolean }>(
    `/v1/auth/admin/students/${student.id}/expel`,
    {
      onSuccess: () => {
        notification.success({
          message: 'Success',
          description: student.expelled
            ? 'Student has been reinstated.'
            : 'Student has been expelled.',
        });
        onUpdate();
      },
      onError: () => {
        notification.error({
          message: 'Error',
          description: 'Failed to update expulsion status.',
        });
      },
    }
  );

  useEffect(() => {
    form.setFieldsValue({
      gradeId: student.gradeId,
      schoolId: student.schoolId,
      shirtSizeId: student.shirtSizeId,
    });
  }, [student, form]);

  const handleSave = (values: StudentUpdatePayload) => {
    updateMutation.mutate(values);
  };

  const handleSubsidyToggle = (checked: boolean) => {
    modal.confirm({
      title: checked
        ? 'Enable Organizational Subsidy?'
        : 'Disable Organizational Subsidy?',
      icon: <ExclamationCircleOutlined />,
      content: checked
        ? 'This will make the student eligible for organizational subsidies.'
        : 'This will remove the student from organizational subsidy eligibility.',
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: () => {
        subsidyToggleMutation.mutate({ eligible: checked });
      },
    });
  };

  const handleSubsidyProgramChange = (programId: number | null) => {
    modal.confirm({
      title: programId ? 'Assign Subsidy Program?' : 'Remove Subsidy Program?',
      icon: <ExclamationCircleOutlined />,
      content: programId
        ? 'This will assign the selected subsidy program to this student.'
        : 'This will remove all subsidy programs from this student.',
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: () => {
        subsidyProgramMutation.mutate({ subsidyProgramId: programId });
      },
    });
  };

  const handleExpelToggle = (checked: boolean) => {
    modal.confirm({
      title: checked ? 'Expel Student?' : 'Reinstate Student?',
      icon: <ExclamationCircleOutlined />,
      content: checked
        ? 'WARNING: This will expel the student from all STAR programs. This action is serious and should only be taken after careful consideration.'
        : 'This will reinstate the student, allowing them to enroll in STAR programs again.',
      okText: 'Confirm',
      okButtonProps: { danger: checked },
      cancelText: 'Cancel',
      onOk: () => {
        expelMutation.mutate({ expelled: checked });
      },
    });
  };

  return (
    <>
      {contextHolder}
      <Row gutter={24}>
        <Col xs={24} md={8}>
          <Card title="Student Photo">
            <div style={{ textAlign: 'center' }}>
              {student.photoUrl ? (
                <Avatar size={150} src={student.photoUrl} />
              ) : (
                <Avatar size={150} icon={<UserOutlined />} />
              )}
              <div style={{ marginTop: 16 }}>
                <Title level={4}>
                  {student.firstName} {student.lastName}
                </Title>
                <Text type="secondary">ID: {student.id}</Text>
              </div>
            </div>
          </Card>

          <Card title="Status" style={{ marginTop: 16 }}>
            {student.expelled && (
              <Tag color="red" style={{ marginBottom: 8 }}>
                EXPELLED
              </Tag>
            )}
            {student.subsidyEligible && (
              <Tag color="green" style={{ marginBottom: 8 }}>
                Subsidy Eligible
              </Tag>
            )}
            {student.subsidyPrograms?.length > 0 && (
              <div>
                {student.subsidyPrograms.map((sp) => (
                  <Tag key={sp.id} color="blue" style={{ marginBottom: 4 }}>
                    {sp.subsidyProgram?.name ||
                      `Program ${sp.subsidyProgramId}`}
                  </Tag>
                ))}
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} md={16}>
          <Card
            title="Basic Information"
            loading={
              updateMutation.isLoading ||
              grades.isLoading ||
              schools.isLoading ||
              shirtSizes.isLoading
            }
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
              initialValues={{
                gradeId: student.gradeId,
                schoolId: student.schoolId,
                shirtSizeId: student.shirtSizeId,
              }}
            >
              <Form.Item
                label="Grade"
                name="gradeId"
                rules={[{ required: true, message: 'Please select a grade' }]}
              >
                <Select placeholder="Select Grade" loading={grades.isLoading}>
                  {grades.data?.map((grade) => (
                    <Option key={grade.id} value={grade.id}>
                      {grade.grade}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="School" name="schoolId">
                <Select
                  placeholder="Select School"
                  loading={schools.isLoading}
                  allowClear
                >
                  {schools.data?.map((school) => (
                    <Option key={school.id} value={school.id}>
                      {school.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Shirt Size" name="shirtSizeId">
                <Select
                  placeholder="Select Shirt Size"
                  loading={shirtSizes.isLoading}
                  allowClear
                >
                  {shirtSizes.data?.map((size) => (
                    <Option key={size.id} value={size.id}>
                      {size.size}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                loading={updateMutation.isLoading}
                block
              >
                Save Changes
              </Button>
            </Form>
          </Card>

          <Card title="Subsidy Settings" style={{ marginTop: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <Text strong>Eligible for Organizational Subsidy</Text>
                  <br />
                  <Text type="secondary">
                    Enables this student to receive organizational subsidies
                  </Text>
                </div>
                <Switch
                  checked={student.subsidyEligible}
                  onChange={handleSubsidyToggle}
                  loading={subsidyToggleMutation.isLoading}
                />
              </div>

              <Divider />

              <div>
                <Text strong>Subsidy Program</Text>
                <br />
                <Text
                  type="secondary"
                  style={{ marginBottom: 8, display: 'block' }}
                >
                  Assign a specific subsidy program to this student
                </Text>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Select Subsidy Program"
                  loading={subsidyPrograms.isLoading}
                  value={
                    student.subsidyPrograms?.[0]?.subsidyProgramId || undefined
                  }
                  onChange={handleSubsidyProgramChange}
                  allowClear
                >
                  {subsidyPrograms.data?.map((program) => (
                    <Option key={program.id} value={program.id}>
                      {program.name}
                    </Option>
                  ))}
                </Select>
              </div>
            </Space>
          </Card>

          {isSiteDirector && (
            <Card
              title="Administrative Actions"
              style={{ marginTop: 16 }}
              headStyle={{ backgroundColor: '#fff1f0' }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <Text strong type="danger">
                    Expel Student
                  </Text>
                  <br />
                  <Text type="secondary">
                    Expelling a student removes them from all STAR programs
                  </Text>
                </div>
                <Switch
                  checked={student.expelled}
                  onChange={handleExpelToggle}
                  loading={expelMutation.isLoading}
                  checkedChildren="Expelled"
                  unCheckedChildren="Active"
                />
              </div>
            </Card>
          )}
        </Col>
      </Row>
    </>
  );
};

export default BasicTab;
